import { cn } from "@/lib/utils";

type LogoSize = "sm" | "md" | "lg" | "xl";
type LogoVariant = "full" | "mark";

type TSGoLogoProps = {
    size?: LogoSize;
    variant?: LogoVariant;
    /** Optional override for the light/dark surface the logo sits on. */
    tone?: "light" | "dark";
    className?: string;
};

const MARK_SIZE: Record<LogoSize, string> = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
    xl: "h-16 w-16",
};

const WORDMARK_SIZE: Record<LogoSize, string> = {
    sm: "text-lg",
    md: "text-xl",
    lg: "text-2xl",
    xl: "text-3xl",
};

/**
 * TSGoLogo — original brand mark.
 *
 * The mark is a rounded-square tile with a stylized "T" sitting above a
 * green "speed dot" (representing rapid delivery). The wordmark uses the
 * Poppins extra-bold cut with a raised comma-dot accent above the "o".
 *
 * Two variants:
 *  • "full" — mark + wordmark (default)
 *  • "mark" — mark only, useful for tight spaces / favicons
 */
export function TSGoLogo({
    size = "md",
    variant = "full",
    tone = "light",
    className,
}: TSGoLogoProps) {
    const isDark = tone === "dark";

    return (
        <div
            data-testid="tsgo-logo"
            className={cn("inline-flex items-center gap-2.5", className)}
        >
            {/* Mark */}
            <div
                aria-hidden
                className={cn(
                    "relative grid place-items-center rounded-2xl shadow-brand",
                    MARK_SIZE[size],
                    isDark
                        ? "bg-white/10 backdrop-blur-md ring-1 ring-white/20"
                        : "bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700",
                )}
            >
                {/* Inner highlight */}
                <span
                    className={cn(
                        "pointer-events-none absolute inset-0 rounded-2xl",
                        isDark
                            ? "bg-gradient-to-b from-white/10 to-transparent"
                            : "bg-gradient-to-b from-white/25 to-transparent",
                    )}
                />
                {/* Stylized T */}
                <svg
                    viewBox="0 0 32 32"
                    className="relative h-[55%] w-[55%]"
                    fill="none"
                    aria-hidden
                >
                    <path
                        d="M6 8h20"
                        stroke="white"
                        strokeWidth="4.5"
                        strokeLinecap="round"
                    />
                    <path
                        d="M16 8v18"
                        stroke="white"
                        strokeWidth="4.5"
                        strokeLinecap="round"
                    />
                    {/* Speed dot */}
                    <circle
                        cx="24"
                        cy="24"
                        r="3"
                        fill={isDark ? "#FFC107" : "#FFC107"}
                    />
                </svg>
            </div>

            {/* Wordmark */}
            {variant === "full" ? (
                <span
                    className={cn(
                        "relative font-extrabold tracking-tight leading-none",
                        WORDMARK_SIZE[size],
                        isDark ? "text-white" : "text-foreground",
                    )}
                >
                    TSGo
                    <span
                        aria-hidden
                        className="absolute -top-1 right-0 h-1.5 w-1.5 rounded-full bg-brand"
                    />
                </span>
            ) : null}
        </div>
    );
}

export default TSGoLogo;
